<template>
  <div class="ve-test">
    <div class="ve-test__header">
      <FormLabel class="ve-test__label">
        {{ $t('page.admin-sync-profiles.test-panel.title') }}
      </FormLabel>
      <div class="ve-test__controls">
        <div class="ve-test__picker">
          <input
            ref="search"
            type="text"
            class="ve-test__search"
            role="combobox"
            aria-autocomplete="list"
            :aria-expanded="dropdownOpen ? 'true' : 'false'"
            :aria-controls="listboxId"
            :aria-activedescendant="activeDescendantId || null"
            v-model="searchQuery"
            :placeholder="
              selectedOpportunity
                ? selectedOpportunity.name
                : $t('page.admin-sync-profiles.test-panel.search-placeholder')
            "
            @input="onSearchInput"
            @focus="onSearchFocus"
            @click="onSearchFocus"
            @blur="onSearchBlur"
            @keydown="onSearchKeydown"
          />
          <div
            v-if="dropdownOpen"
            :id="listboxId"
            role="listbox"
            class="ve-test__dropdown"
            :style="dropdownStyle"
            @mousedown.prevent
          >
            <div v-if="searching" class="ve-test__dropdown-empty">
              {{ $t('page.admin-sync-profiles.test-panel.searching') }}
            </div>
            <div v-else-if="results.length === 0" class="ve-test__dropdown-empty">
              {{ $t('page.admin-sync-profiles.test-panel.no-results') }}
            </div>
            <div
              v-for="(opp, idx) in results"
              :key="opp.id"
              :id="optionId(opp)"
              role="option"
              :aria-selected="idx === activeIndex ? 'true' : 'false'"
              :class="[
                've-test__option',
                {
                  've-test__option--current': isCurrentOpportunity(opp),
                  've-test__option--active': idx === activeIndex,
                },
              ]"
              @mouseenter="activeIndex = idx"
              @click="select(opp)"
            >
              <span class="ve-test__option-name">{{ opp.name }}</span>
              <span v-if="isCurrentOpportunity(opp)" class="ve-test__option-check" aria-hidden="true">✓</span>
            </div>
          </div>
        </div>
        <button type="button" class="ve-test__button" :disabled="!canTest || loading" @click="runTest">
          <span v-if="loading" class="ve-test__spinner" aria-hidden="true" />
          {{ $t('page.admin-sync-profiles.test-panel.test-button') }}
        </button>
      </div>
    </div>

    <!-- Result panel -->
    <div v-if="displayState === 'error' || httpError" class="ve-test__result ve-test__result--error">
      <div class="ve-test__result-title">
        {{ $t('page.admin-sync-profiles.test-panel.error-title') }}
      </div>
      <pre class="ve-test__result-body">{{ httpError || parseError }}</pre>
    </div>
    <div v-else-if="displayState === 'omit'" class="ve-test__result ve-test__result--omit">
      <div class="ve-test__result-title">
        {{ $t('page.admin-sync-profiles.test-panel.omit-title') }}
      </div>
      <p class="ve-test__result-body">{{ $t('page.admin-sync-profiles.test-panel.omit-detail') }}</p>
    </div>
    <div v-else-if="displayState === 'success'" class="ve-test__result ve-test__result--success">
      <div class="ve-test__result-title">
        <span class="ve-test__result-badge">{{ resultType }}</span>
      </div>
      <pre v-if="result === ''" class="ve-test__result-body ve-test__result-body--empty">
        {{ $t('page.admin-sync-profiles.test-panel.empty-string') }}
      </pre>
      <pre v-else class="ve-test__result-body">{{ result }}</pre>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import FormLabel from '@/components/form/FormLabel';
import { mapGetters } from 'vuex';
import { opportunityService } from '@/services/opportunity.service';
import { opportunitySyncProfilesService } from '@/services/opportunity-sync-profiles.service';

const STORAGE_PREFIX = 'value-expression-test:opportunity:';

export default {
  name: 'ValueExpressionTestPanel',
  components: { FormLabel },
  props: {
    valueExpression: { type: String, default: '' },
    // When set, the last-used opportunity is remembered per (user, profile) so each
    // profile keeps its own preferred test target. Without it, falls back to per-user.
    profileId: { type: String, default: null },
  },
  data() {
    return {
      searchQuery: '',
      searchFocused: false,
      results: [],
      searching: false,
      // Index of the keyboard-highlighted option; resets on results change and on mouseenter.
      activeIndex: 0,
      selectedOpportunity: null, // { id, name }
      loading: false,
      result: null,
      resultType: null,
      wouldOmit: false,
      parseError: null,
      httpError: null,
      // Inline style for the dropdown — computed viewport-relative so it escapes
      // BaseDialog's overflow:auto content area instead of being clipped.
      dropdownStyle: { display: 'none' },
    };
  },
  computed: {
    ...mapGetters('auth', ['currentUser']),
    canTest() {
      return !!(this.selectedOpportunity && (this.valueExpression || '').trim() !== '');
    },
    dropdownOpen() {
      // Show whenever the input is focused AND there's something to display
      // (results, loading state, or empty-state message).
      return this.searchFocused && (this.searching || this.results.length > 0 || this.searchQuery.length > 0);
    },
    displayState() {
      if (this.httpError) return 'error';
      if (this.parseError) return 'error';
      if (this.wouldOmit) return 'omit';
      if (this.result !== null) return 'success';
      return null;
    },
    storageKey() {
      const userId = this.currentUser && this.currentUser.id ? this.currentUser.id : 'anon';
      const suffix = this.profileId ? `:${this.profileId}` : '';
      return `${STORAGE_PREFIX}${userId}${suffix}`;
    },
    // Per-instance ARIA IDs — `this._uid` keeps them unique if multiple panels mount.
    listboxId() {
      return `ve-test-listbox-${this._uid}`;
    },
    activeDescendantId() {
      if (!this.dropdownOpen) return null;
      const opp = this.results[this.activeIndex];
      return opp ? this.optionId(opp) : null;
    },
  },
  mounted() {
    this.restoreLastOpportunity();
    // Track viewport / ancestor scrolls + resize so the fixed-positioned dropdown
    // follows the input even when the field-mapping dialog scrolls underneath it.
    this._onReflow = () => {
      if (this.dropdownOpen) this.positionDropdown();
    };
    window.addEventListener('resize', this._onReflow);
    window.addEventListener('scroll', this._onReflow, true);
  },
  beforeDestroy() {
    // Cancel any pending debounce timer so a late-firing search can't touch state on
    // a torn-down instance. (Stored directly on `this` — not in data() — to keep it
    // out of Vue's reactivity system and avoid the `vue/no-reserved-keys` rule.)
    if (this._searchTimer) clearTimeout(this._searchTimer);
    if (this._searchCancelSource) this._searchCancelSource.cancel('Component destroyed');
    if (this._onReflow) {
      window.removeEventListener('resize', this._onReflow);
      window.removeEventListener('scroll', this._onReflow, true);
    }
  },
  watch: {
    dropdownOpen(visible) {
      if (visible) this.$nextTick(() => this.positionDropdown());
    },
    results() {
      // New result set — reset the keyboard cursor to the top.
      this.activeIndex = 0;
      if (this.dropdownOpen) this.$nextTick(() => this.positionDropdown());
    },
  },
  methods: {
    onSearchFocus() {
      this.searchFocused = true;
      // Eagerly load suggestions on focus — with no query this surfaces the most
      // recent opportunities (newest first), giving the user a starter set to pick
      // from without having to type anything. Skip if a fetch is already in flight —
      // clicking an unfocused input fires both `focus` and `click`, which would
      // otherwise double-fire runSearch (the first hasn't populated `results` yet).
      if (this.results.length === 0 && !this.searching) {
        this.runSearch();
      }
      this.$nextTick(() => this.positionDropdown());
    },
    onSearchBlur() {
      // Defer to let a click on an option fire before we hide.
      setTimeout(() => {
        this.searchFocused = false;
      }, 120);
    },
    onSearchInput() {
      // Typing should always reopen the dropdown — after a select we cleared searchFocused
      // to hide the picker, but the input never lost real focus, so the next `focus` event
      // won't fire. Force it back true on any keystroke.
      this.searchFocused = true;
      clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => this.runSearch(), 200);
      this.$nextTick(() => this.positionDropdown());
    },
    onSearchKeydown(event) {
      // Escape always closes the dropdown, even when there are no results to navigate.
      if (event.key === 'Escape') {
        event.preventDefault();
        this.searchFocused = false;
        return;
      }
      if (!this.dropdownOpen || this.results.length === 0) return;
      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault();
          this.activeIndex = (this.activeIndex + 1) % this.results.length;
          this.$nextTick(() => this.scrollActiveIntoView());
          break;
        case 'ArrowUp':
          event.preventDefault();
          this.activeIndex = (this.activeIndex - 1 + this.results.length) % this.results.length;
          this.$nextTick(() => this.scrollActiveIntoView());
          break;
        case 'Enter': {
          event.preventDefault();
          const opp = this.results[this.activeIndex];
          if (opp) this.select(opp);
          break;
        }
      }
    },
    scrollActiveIntoView() {
      const active = this.$el && this.$el.querySelector('.ve-test__option--active');
      if (active && typeof active.scrollIntoView === 'function') {
        active.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    },
    optionId(opp) {
      return `ve-test-option-${this._uid}-${opp.id}`;
    },
    positionDropdown() {
      const el = this.$refs.search;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const viewportH = window.innerHeight;
      const padding = 8;
      const preferredMax = 240;
      const spaceBelow = Math.max(0, viewportH - rect.bottom - padding);
      const spaceAbove = Math.max(0, rect.top - padding);
      const openDown = spaceBelow >= 160 || spaceBelow >= spaceAbove;
      const maxHeight = Math.min(preferredMax, openDown ? spaceBelow : spaceAbove);
      this.dropdownStyle = openDown
        ? {
            position: 'fixed',
            top: `${rect.bottom + 2}px`,
            left: `${rect.left}px`,
            width: `${rect.width}px`,
            maxHeight: `${maxHeight}px`,
          }
        : {
            position: 'fixed',
            bottom: `${viewportH - rect.top + 2}px`,
            left: `${rect.left}px`,
            width: `${rect.width}px`,
            maxHeight: `${maxHeight}px`,
          };
    },
    runSearch() {
      const q = (this.searchQuery || '').trim();
      // Empty query → fetch the latest opportunities (used as the "default suggestions"
      // when the picker is focused with nothing typed). Non-empty query → server filters.
      // Per-instance cancel source so picker requests don't stomp on other components'
      // in-flight `getOpportunities` calls (e.g. the main Opportunities list page).
      // Cancel the picker's own previous request first; the seq guard below is the
      // belt-and-suspenders catch for any response that slipped through before cancel.
      if (this._searchCancelSource) this._searchCancelSource.cancel('Newer search');
      this._searchCancelSource = axios.CancelToken.source();
      this._searchSeq = (this._searchSeq || 0) + 1;
      const seq = this._searchSeq;
      this.searching = true;
      opportunityService
        .getOpportunities({
          page: 1,
          pageSize: 5,
          sortingField: 'created_at',
          sortingOrder: 'desc',
          filters: q ? { query: q } : {},
          cancelToken: this._searchCancelSource,
        })
        .then(({ opportunities }) => {
          if (seq !== this._searchSeq) return;
          // Server already capped at 5 via pageSize — small enough to scan at a glance.
          this.results = opportunities || [];
        })
        .catch(() => {
          if (seq !== this._searchSeq) return;
          this.results = [];
        })
        .finally(() => {
          if (seq !== this._searchSeq) return;
          this.searching = false;
        });
    },
    isCurrentOpportunity(opp) {
      return !!this.selectedOpportunity && opp.id === this.selectedOpportunity.id;
    },
    select(opp) {
      this.selectedOpportunity = { id: opp.id, name: opp.name };
      this.searchQuery = '';
      this.results = [];
      this.searchFocused = false;
      this.persistLastOpportunity(opp);
      this.clearResult();
    },
    persistLastOpportunity(opp) {
      try {
        localStorage.setItem(this.storageKey, JSON.stringify({ id: opp.id, name: opp.name }));
      } catch {
        // localStorage may be full or blocked; non-fatal.
      }
    },
    restoreLastOpportunity() {
      let stored;
      try {
        const raw = localStorage.getItem(this.storageKey);
        stored = raw ? JSON.parse(raw) : null;
      } catch {
        stored = null;
      }
      if (!stored || !stored.id) return;
      // Optimistically show the cached name so the user sees "testing against X" right away.
      this.selectedOpportunity = { id: stored.id, name: stored.name };
      // Refresh the name (and verify the opp still exists) in the background. If it's
      // gone (deleted, moved tenant), drop the selection silently — the Test button
      // will reveal the issue if the user actually clicks it.
      opportunityService
        .getOpportunity({ id: stored.id })
        .then((opp) => {
          if (opp && opp.id === stored.id) {
            this.selectedOpportunity = { id: opp.id, name: opp.name };
          }
        })
        .catch(() => {
          // Keep the cached selection — if it's truly stale, the preview call will 404.
        });
    },
    clearResult() {
      this.result = null;
      this.resultType = null;
      this.wouldOmit = false;
      this.parseError = null;
      this.httpError = null;
    },
    runTest() {
      if (!this.canTest) return;
      this.clearResult();
      this.loading = true;
      opportunitySyncProfilesService
        .previewValueExpression({
          valueExpression: this.valueExpression,
          opportunityId: this.selectedOpportunity.id,
        })
        .then((data) => {
          // The endpoint returns 200 with one of {result, wouldOmit, parseError}.
          this.result = data && data.result != null ? data.result : data && data.result === '' ? '' : null;
          this.resultType = (data && data.resultType) || null;
          this.wouldOmit = !!(data && data.wouldOmit);
          this.parseError = (data && data.parseError) || null;
        })
        .catch((err) => {
          const status = err && err.response && err.response.status;
          if (status === 404) {
            this.httpError = this.$t('page.admin-sync-profiles.test-panel.opportunity-not-found');
          } else if (status === 403) {
            this.httpError = this.$t('page.admin-sync-profiles.test-panel.not-allowed');
          } else if (status === 400) {
            this.httpError = this.$t('page.admin-sync-profiles.test-panel.invalid-request');
          } else {
            // Unknown / network error — surface the back-end message if present, else generic.
            const msg =
              (err && err.response && err.response.data && (err.response.data.message || err.response.data.error)) ||
              null;
            this.httpError = msg || this.$t('page.admin-sync-profiles.test-panel.unknown-error');
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style lang="scss">
.ve-test {
  border-top: 1px solid $color-border;
  padding-top: 12px;
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  &__header {
    display: flex;
    flex-direction: column;
    gap: 6px;
  }

  &__label {
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: $color-normal-pale;
    margin: 0;
  }

  &__controls {
    display: flex;
    gap: 10px;
    align-items: center;
  }

  &__picker {
    position: relative;
    flex: 1 1 auto;
    min-width: 0;
  }

  &__search {
    width: 100%;
    height: 36px;
    padding: 0 10px;
    border: 1px solid $color-border;
    border-radius: 4px;
    font-size: 13px;
    background: $color-neutral-pale;
    color: $color-normal;

    &:focus {
      outline: none;
      border-color: $color-accent;
      background: $color-white;
    }
  }

  &__dropdown {
    // Placement is driven by inline :style (position: fixed, viewport-relative) so
    // BaseDialog's overflow:auto content area doesn't clip it.
    background: $color-white;
    border: 1px solid $color-border;
    border-radius: 6px;
    box-shadow: 0 8px 20px rgba(0, 0, 0, 0.12);
    z-index: 2000; // above the Vuetify dialog stack
    overflow-y: auto;
  }

  &__dropdown-empty {
    padding: 8px 12px;
    color: $color-normal-pale;
    font-size: 13px;
    font-style: italic;
  }

  &__option {
    padding: 6px 12px;
    cursor: pointer;
    font-size: 13px;
    color: $color-normal;
    display: flex;
    align-items: center;
    gap: 8px;

    // Mouse hover and keyboard-active share the same highlight, so navigation modes agree.
    &:hover,
    &--active {
      background: $color-neutral-pale;
    }

    &--current {
      background: $color-accent-100;
      font-weight: 600;

      &:hover,
      &.ve-test__option--active {
        background: $color-accent-100;
      }
    }
  }

  &__option-name {
    flex: 1 1 auto;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__option-check {
    color: $color-accent;
    font-weight: 700;
    flex: 0 0 auto;
  }

  &__button {
    flex: 0 0 auto;
    height: 36px;
    padding: 0 18px;
    border: 1px solid $color-accent;
    border-radius: 4px;
    background: $color-accent;
    color: $color-white;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: opacity 150ms ease;

    &:disabled {
      opacity: 0.45;
      cursor: not-allowed;
    }

    &:not(:disabled):hover {
      opacity: 0.9;
    }
  }

  &__spinner {
    display: inline-block;
    width: 12px;
    height: 12px;
    border: 2px solid rgba(255, 255, 255, 0.6);
    border-top-color: $color-white;
    border-radius: 50%;
    animation: ve-test-spin 0.7s linear infinite;
  }

  &__result {
    border: 1px solid $color-border;
    border-radius: 4px;
    background: $color-neutral-pale;
    padding: 10px 12px;

    &--success {
      border-color: rgba($color-success, 0.4);
      background: $color-success-pale;
    }

    &--omit {
      border-color: rgba($color-orange, 0.4);
      background: $color-yellow-pale;
    }

    &--error {
      border-color: rgba($color-error, 0.5);
      background: $color-error-pale;
    }
  }

  &__result-title {
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: $color-normal-pale;
    margin-bottom: 6px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  &__result-badge {
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 11px;
    background: $color-white;
    border: 1px solid $color-border;
    border-radius: 3px;
    padding: 1px 6px;
    color: $color-normal;
    letter-spacing: 0;
    text-transform: none;
    font-weight: 600;
  }

  &__result-body {
    margin: 0;
    font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, monospace;
    font-size: 12.5px;
    line-height: 1.5;
    color: $color-normal;
    background: none;
    border: none;
    padding: 0;
    // Long results (e.g. tender.description) — scroll inside the panel.
    max-height: 200px;
    overflow: auto;
    white-space: pre-wrap;
    word-break: break-word;

    &--empty {
      font-style: italic;
      color: $color-normal-pale;
    }
  }
}

@keyframes ve-test-spin {
  to {
    transform: rotate(360deg);
  }
}
</style>
